/**
 * Types and fetchers for user-defined custom fields (V5-14 sub-item 4).
 *
 * One module so the admin manager, the entry-form inputs and the records tab
 * cannot drift in what they think the API returns. Everything here follows the
 * repo's `apiFetch` + `res.ok` + `{ error }`-envelope convention rather than
 * throwing from a shared wrapper.
 *
 * MONEY IS PAISE ON THIS BOUNDARY
 * -------------------------------
 * A `number` field with `isMoney` sends and receives an INTEGER count of paise,
 * exactly like `amountPaise` everywhere else in AIM. `rupeesToPaise` /
 * `paiseToRupees` are the only two places that divide, and the input component
 * is the only thing that calls them — nothing else in this feature should ever
 * see a rupee float.
 */
import { apiFetch } from "@/lib/api/client";
import { apiUrl, type QueryValue } from "@/lib/api/config";

export type CustomFieldType = "text" | "number" | "date" | "option_set";
export type CustomFieldEntityType = "income" | "expense" | "transfer";

export const FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: "Text",
  number: "Number",
  date: "Date",
  option_set: "Choice list",
};

export const ENTITY_TYPE_LABELS: Record<CustomFieldEntityType, string> = {
  income: "Income",
  expense: "Expenses",
  transfer: "Transfers",
};

export const ENTITY_TYPES: CustomFieldEntityType[] = ["income", "expense", "transfer"];

export interface CustomFieldOption {
  id: string;
  key: string;
  label: string;
  color: string | null;
  sortOrder: number;
}

export interface CustomField {
  id: string;
  key: string;
  label: string;
  description: string | null;
  fieldType: CustomFieldType;
  entityTypes: CustomFieldEntityType[];
  isRequired: boolean;
  sortOrder: number;
  optionSetId: string | null;
  optionSetKey: string | null;
  optionSetLabel: string | null;
  /** The OFFER list — archived options are already filtered out server-side. */
  options: CustomFieldOption[];
  isMoney: boolean;
  isSystem: boolean;
  isArchived: boolean;
  createdById: string | null;
}

export interface CustomFieldValue {
  entityType: CustomFieldEntityType;
  entityId: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: CustomFieldType;
  isMoney: boolean;
  /**
   * True once the FIELD was retired. The value still resolves — that is the
   * archiving rule, not a bug — so this is a styling hint, never a reason to
   * drop the row.
   */
  fieldIsArchived: boolean;
  value: string | number | null;
  optionId: string | null;
  optionLabel: string | null;
  optionColor: string | null;
  optionIsArchived: boolean;
}

export interface CustomFieldRecordMatch {
  entityType: CustomFieldEntityType;
  entityId: string;
  date: string;
  amountPaise: number;
  description: string | null;
  categoryName: string | null;
  values: CustomFieldValue[];
}

/** The comparison operators the backend accepts, per field type. */
export const OPS_FOR_TYPE: Record<CustomFieldType, { value: string; label: string }[]> = {
  text: [
    { value: "eq", label: "is" },
    { value: "neq", label: "is not" },
    { value: "contains", label: "contains" },
    { value: "startswith", label: "starts with" },
    { value: "set", label: "has any value" },
    { value: "unset", label: "is empty" },
  ],
  number: [
    { value: "eq", label: "=" },
    { value: "neq", label: "≠" },
    { value: "gt", label: ">" },
    { value: "gte", label: "≥" },
    { value: "lt", label: "<" },
    { value: "lte", label: "≤" },
    { value: "set", label: "has any value" },
    { value: "unset", label: "is empty" },
  ],
  date: [
    { value: "eq", label: "on" },
    { value: "neq", label: "not on" },
    { value: "gt", label: "after" },
    { value: "gte", label: "on or after" },
    { value: "lt", label: "before" },
    { value: "lte", label: "on or before" },
    { value: "set", label: "has any value" },
    { value: "unset", label: "is empty" },
  ],
  option_set: [
    { value: "eq", label: "is" },
    { value: "neq", label: "is not" },
    { value: "set", label: "has any value" },
    { value: "unset", label: "is empty" },
  ],
};

/** Operators that take no operand — the value input hides for these. */
export const VALUELESS_OPS = new Set(["set", "unset"]);

export interface CustomFieldFilter {
  field: string;
  op: string;
  value: string;
}

// ── money ────────────────────────────────────────────────────────────────────

/** `"1250.50"` -> `125050`. Returns null when the input is not a clean amount. */
export function rupeesToPaise(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (!/^-?\d*(\.\d{0,2})?$/.test(trimmed)) return null;
  const paise = Math.round(Number(trimmed) * 100);
  return Number.isFinite(paise) ? paise : null;
}

/** `125050` -> `"1250.50"`. */
export function paiseToRupees(paise: number): string {
  return (paise / 100).toFixed(2);
}

/** How a value reads in a table cell. Money renders as rupees; options as labels. */
export function formatValue(value: CustomFieldValue): string {
  if (value.value === null || value.value === undefined) return "";
  if (value.fieldType === "option_set") return value.optionLabel ?? String(value.value);
  if (value.fieldType === "number" && value.isMoney) {
    // The API speaks paise; a human-facing cell speaks rupees, grouped the
    // Indian way like every other amount in AIM.
    return `₹${(Number(value.value) / 100).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  return String(value.value);
}

// ── fetchers ─────────────────────────────────────────────────────────────────

async function unwrap(res: Response, fallback: string) {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || fallback);
  }
  return res.json();
}

export async function fetchFields(params: {
  entityType?: CustomFieldEntityType;
  includeArchived?: boolean;
  all?: boolean;
}): Promise<CustomField[]> {
  const query: Record<string, QueryValue> = {};
  if (params.entityType) query.entityType = params.entityType;
  if (params.includeArchived) query.includeArchived = "true";
  if (params.all) query.all = "true";
  const body = await unwrap(
    await apiFetch(apiUrl("customFields", "", query)),
    "Failed to load custom fields",
  );
  return (body.fields ?? []) as CustomField[];
}

export interface CustomFieldCreateInput {
  label: string;
  fieldType: CustomFieldType;
  entityTypes: CustomFieldEntityType[];
  description?: string | null;
  isRequired: boolean;
  isMoney: boolean;
  sortOrder: number;
  optionSetRef?: string | null;
}

export async function createField(input: CustomFieldCreateInput): Promise<CustomField> {
  return unwrap(
    await apiFetch(apiUrl("customFields"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }),
    "Failed to create custom field",
  );
}

export interface CustomFieldUpdateInput {
  label?: string;
  description?: string | null;
  entityTypes?: CustomFieldEntityType[];
  isRequired?: boolean;
  sortOrder?: number;
  isArchived?: boolean;
}

export async function updateField(
  id: string,
  input: CustomFieldUpdateInput,
): Promise<CustomField> {
  return unwrap(
    await apiFetch(apiUrl("customFields", `/${id}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }),
    "Failed to update custom field",
  );
}

export async function deleteField(id: string): Promise<void> {
  const res = await apiFetch(apiUrl("customFields", `/${id}`), { method: "DELETE" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    // 409 carries the "archive it instead" sentence, which is the whole
    // teaching moment — surface it verbatim rather than a generic failure.
    throw new Error(err.error || "Failed to delete custom field");
  }
}

export async function fetchValues(
  entityType: CustomFieldEntityType,
  entityId: string,
): Promise<CustomFieldValue[]> {
  const body = await unwrap(
    await apiFetch(apiUrl("customFields", `/values/${entityType}/${entityId}`)),
    "Failed to load custom field values",
  );
  return (body.values ?? []) as CustomFieldValue[];
}

/** Values for many records in one request — what a ledger screen uses. */
export async function fetchValuesBatch(
  entityType: CustomFieldEntityType,
  entityIds: string[],
): Promise<Record<string, CustomFieldValue[]>> {
  if (entityIds.length === 0) return {};
  const body = await unwrap(
    await apiFetch(apiUrl("customFields", "/values/batch"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entityType, entityIds }),
    }),
    "Failed to load custom field values",
  );
  return (body.values ?? {}) as Record<string, CustomFieldValue[]>;
}

export async function saveValues(
  entityType: CustomFieldEntityType,
  entityId: string,
  values: { fieldKey: string; value: string | number | null }[],
): Promise<CustomFieldValue[]> {
  if (values.length === 0) return [];
  const body = await unwrap(
    await apiFetch(apiUrl("customFields", "/values"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entityType, entityId, values }),
    }),
    "Failed to save custom fields",
  );
  return (body.values ?? []) as CustomFieldValue[];
}

/**
 * The filter query string, built once and shared by the table and the export so
 * the exported set is provably the visible set.
 *
 * Three PARALLEL repeated params (`field` / `op` / `value`), which is what the
 * backend's `_parse_filters` zips positionally.
 */
export function buildFilterParams(
  entityType: CustomFieldEntityType,
  filters: CustomFieldFilter[],
): URLSearchParams {
  const params = new URLSearchParams();
  params.set("entityType", entityType);
  for (const filter of filters) {
    if (!filter.field) continue;
    params.append("field", filter.field);
    params.append("op", filter.op || "eq");
    params.append("value", VALUELESS_OPS.has(filter.op) ? "" : filter.value);
  }
  return params;
}

export async function fetchFilteredRecords(
  entityType: CustomFieldEntityType,
  filters: CustomFieldFilter[],
): Promise<{ count: number; records: CustomFieldRecordMatch[] }> {
  const params = buildFilterParams(entityType, filters);
  const body = await unwrap(
    await apiFetch(`${apiUrl("customFields", "/records")}?${params.toString()}`),
    "Failed to filter records",
  );
  return { count: body.count ?? 0, records: (body.records ?? []) as CustomFieldRecordMatch[] };
}

/**
 * Download the filtered set as CSV.
 *
 * Blob + synthetic anchor rather than `window.open`, so the request carries the
 * session the way every other `apiFetch` does — the pattern `RecordsFilterBar`
 * uses and the one this repo prefers for new code.
 */
export async function downloadExport(
  entityType: CustomFieldEntityType,
  filters: CustomFieldFilter[],
): Promise<void> {
  const params = buildFilterParams(entityType, filters);
  const res = await apiFetch(`${apiUrl("customFields", "/export")}?${params.toString()}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Export failed");
  }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = `aim-${entityType}-custom-fields.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}
